import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';
import './Appointments.css';
import '../Tables.css';
import Sidebar from '../Sidebar';
import InternalHeader from '../InternalHeader';
import { FaCalendar, FaTrash, FaCog, FaUpload, FaArchive } from 'react-icons/fa';
import { format } from 'date-fns';
import Actions from '../Actions/Actions';
import axios from 'axios';

const AppointmentsUser = () => {
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [date, setDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewOption, setViewOption] = useState('all'); // State for filtering by status
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]); // Track selected rows
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState({ email: '' });

    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const handleDateChange = (date) => {
        setDate(date);
    };

    useEffect(() => {
        const fetchCurrentUser = async () => {
          try {
            const res = await fetch('https://api.hgcdental.clinic/api/profile', {
              credentials: 'include', // ⬅️ this is key!
            });
            if (!res.ok) throw new Error('Failed to fetch current user');
            const data = await res.json();
            setCurrentUser({ email: data.email });
          } catch (err) {
            console.error('Failed to fetch current user', err);
          }
        };
      
        fetchCurrentUser();
      }, []);      

    const fetchAppointments = async () => {
        try {
            const response = await axios.get('https://api.hgcdental.clinic/api/appointments/');
            const userAppointments = response.data.filter(
                (appt) => appt.patientID === currentUser.patientID
            );
            setAppointments(userAppointments);
        } catch (error) {
            console.error('Error fetching appointments:', error);
        }
    };

    // Fetch appointments from the API
    useEffect(() => {
        const fetchAppointments = async () => {
            try {
                const response = await axios.get('https://api.hgcdental.clinic/api/appointments/');
                const userAppointments = response.data.filter(
                    (appt) => appt.email === currentUser.email
                );
                setAppointments(userAppointments);
            } catch (error) {
                console.error('Error fetching appointments:', error);
            }
        };

        fetchAppointments();
    }, [currentUser.email]);

    // Columns for the DataTable
    const columns = [
        { name: 'Patient Name', selector: row => row.patientName, sortable: true, filterable: true },
        { name: 'Email', selector: row => row.email, sortable: true, filterable: true },
        { name: 'Reason', selector: row => row.reason, sortable: true, filterable: true },
        {
            name: 'Date',
            selector: row => format(new Date(row.date), 'MMMM dd, yyyy'),
            sortable: true,
            filterable: true
        },
        { name: 'Start Time', selector: row => row.startTime, sortable: true, filterable: true },
        { name: 'End Time', selector: row => row.endTime, sortable: true, filterable: true },
        { name: 'Dentist', selector: row => row.dentist, sortable: true, filterable: true },
        { name: 'Status', selector: row => row.status || 'Pending', sortable: true, filterable: true },
    ];

    // Handle pagination changes
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    // Handle rows per page changes
    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    // Handle search input changes
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    // Filter appointments based on search term and view option
    const filteredData = appointments.filter(appointment => {
        // Filter by viewOption (status or archived)
        if (viewOption === 'Checked In') {
            return appointment.status === 'Checked In';
        } else if (viewOption === 'Completed') {
            return appointment.status === 'Completed';
        } else if (viewOption === 'Cancelled') {
            return appointment.status === 'Cancelled';
        } else if (viewOption === 'Booked') {
            return appointment.status === 'Booked';
        } else if (viewOption === 'Archived') {
            return appointment.isArchived === true; // Show only archived appointments
        } else {
            return appointment.isArchived !== true; // Hide archived appointments by default
        }
    }).filter(appointment =>
        // Filter by search term
        (appointment.patientName && appointment.patientName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (appointment.email && appointment.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (appointment.reason && appointment.reason.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    // Paginate the filtered data
    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    // Calculate total pages for pagination
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // Handle row click to navigate to appointment details
    const handleRowClick = (row) => {
        navigate(`/appointment-details/${row._id}`);
    };

    // Handle archiving selected appointments
    const handleDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            alert('No appointments selected.');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to archive the selected appointments?');
        if (!confirmArchive) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/appointments/archive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ appointmentIds: selectedRows }),
            });

            if (!response.ok) {
                throw new Error('Failed to archive appointments');
            }

            const data = await response.json();
            console.log('Appointments archived successfully:', data);
            alert('Appointments archived successfully');
            // Refresh the appointments list
            fetchAppointments();

            // Clear selected rows
            setSelectedRows([]);

        } catch (error) {
            console.error('Error archiving appointments:', error);
            setError('Failed to archive appointments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleUnDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            alert('No appointments selected.');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to unarchive the selected appointments?');
        if (!confirmArchive) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/appointments/unarchive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ appointmentIds: selectedRows }),
            });

            if (!response.ok) {
                throw new Error('Failed to unarchive appointments');
            }

            const data = await response.json();
            console.log('Appointments unarchived successfully:', data);
            alert('Appointments unarchived successfully');
            // Refresh the appointments list
            fetchAppointments();

            // Clear selected rows
            setSelectedRows([]);

        } catch (error) {
            console.error('Error unarchiving appointments:', error);
            setError('Failed to unarchive appointments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle generating CSV (placeholder function)
    const handleGenerateCSV = () => {
        // Add logic to generate CSV
    };

    // Handle selected rows change
    const handleSelectedRowsChange = ({ selectedRows }) => {
        setSelectedRows(selectedRows.map(row => row._id));
    };

    // Handle closing the modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // Handle opening the modal
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    // Custom styles for the DataTable
    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className={`appointments-page ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
            <InternalHeader moduleName="Appointments" toggleSidebar={toggleSidebar} />
            <div className="appointments-container">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="appointments-container2">
                    <div className="data-table-header">
                        <div className="flex-div">
                            <a href="/create-appointment-user">
                                <button className="create-button">New</button>
                            </a>
                        </div>
                        <a href="/appointments-users-calendar">
                            <button className="view-toggle-button"><FaCalendar /></button>
                        </a>
                    </div>
                    <div className="table-form">
                        <div>
                            <h2>Your Appointments</h2>
                            {appointments.length === 0 ? (
                                <p>No appointments found.</p>
                            ) : (
                                <ul>
                                    {appointments.map((appt) => (
                                        <li key={appt._id}>
                                            Appointment on <strong>{formatDate(appt.date)}</strong> for <strong>{appt.reason}</strong> with <strong>{appt.dentist}</strong>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentsUser;